import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import StudyDataStep from './StudyDataStep';
import DeterminationsStep from './DeterminationsStep';
import type { DeterminationRow } from './DeterminationsStep';
import ResultStep from './ResultStep';
import {
  getStudyDetail,
  getStudyOrderInfo,
  saveStudyResult,
  getErrorMessage,
} from '../../../services/StudyResultsController';
import type {
  StudyDetail,
  ProfessionalOption,
  UploadedFile,
  SaveStudyResultRequest,
} from '../../../services/StudyResultsController';
import './StudyResultWizard.scss';

// ── Props ────────────────────────────────────────────────────────────

export interface StudyResultWizardProps {
  visible: boolean;
  onHide: () => void;
  healthPatientServiceId: number;
  patientId: number;
  onSaved: () => void;
  /** Current user's professional ID — used to pre-fill "Solicitado por" in new mode */
  professionalId?: number;
  /** Current user's professional display name */
  professionalName?: string;
  /** When coming from a study order, pre-fills study type and diagnosis */
  studyOrderId?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────

const LABORATORY_TYPE_ID = 1;

/** Logical step indices (always 0-1-2 regardless of whether lab is active) */
const STEP_STUDY = 0;
const STEP_DETERMINATIONS = 1;
const STEP_RESULT = 2;

function parseISODate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function formatDateISO(date: Date | null): string {
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ── Step definitions ─────────────────────────────────────────────────

interface StepDef {
  key: string;
  label: string;
}

const ALL_STEPS: StepDef[] = [
  { key: 'study', label: 'Datos del estudio' },
  { key: 'determinations', label: 'Determinaciones' },
  { key: 'result', label: 'Resultado' },
];

const STEPS_NO_LAB: StepDef[] = [
  { key: 'study', label: 'Datos del estudio' },
  { key: 'result', label: 'Resultado' },
];

// ── Component ────────────────────────────────────────────────────────

export default function StudyResultWizard({
  visible,
  onHide,
  healthPatientServiceId,
  patientId,
  onSaved,
  professionalId: propProfessionalId,
  professionalName: propProfessionalName,
  studyOrderId,
}: StudyResultWizardProps) {
  const toast = useRef<Toast>(null);

  // ── Loading ──
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [studyDetail, setStudyDetail] = useState<StudyDetail | null>(null);

  // ── Wizard step (index into the `steps` array) ──
  const [currentStep, setCurrentStep] = useState(0);

  // ── Step 0: Study data ──
  const [studyTypeId, setStudyTypeId] = useState<number | null>(null);
  const [serviceDate, setServiceDate] = useState<Date | null>(null);
  const [orderedByOption, setOrderedByOption] = useState<ProfessionalOption | null>(null);
  const [externalId, setExternalId] = useState('');
  const [presumptiveDiagnostic, setPresumptiveDiagnostic] = useState('');

  // ── Step 1: Determinations (lab only) ──
  const [determinations, setDeterminations] = useState<DeterminationRow[]>([]);

  // ── Step 2: Result ──
  const [stateTypeId, setStateTypeId] = useState<number | null>(null);
  const [resultDate, setResultDate] = useState<Date | null>(null);
  const [resultDescription, setResultDescription] = useState('');
  const [resultText, setResultText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // ── Derived ──
  const isLaboratory = studyTypeId === LABORATORY_TYPE_ID;
  const steps = useMemo<StepDef[]>(
    () => (isLaboratory ? ALL_STEPS : STEPS_NO_LAB),
    [isLaboratory],
  );
  const isLastStep = currentStep === steps.length - 1;
  const currentStepKey = steps[currentStep]?.key;

  const existingFiles = useMemo(
    () => studyDetail?.existingFiles ?? [],
    [studyDetail],
  );

  // ── Whether we're creating a brand-new study result ──
  const isNewMode = healthPatientServiceId === 0;

  // ── Load study detail on open (skip for new mode) ──
  useEffect(() => {
    if (!visible) return;

    setCurrentStep(0);
    setDeterminations([]);
    setUploadedFiles([]);

    if (isNewMode) {
      // New study: start with defaults
      setStudyDetail(null);
      setStudyTypeId(null);
      setServiceDate(new Date());
      setOrderedByOption(
        propProfessionalId ? { id: propProfessionalId, name: propProfessionalName ?? '' } : null,
      );
      setExternalId('');
      setPresumptiveDiagnostic('');
      setStateTypeId(null);
      setResultDate(new Date());
      setResultDescription('');
      setResultText('');

      // If coming from a study order, fetch its info to pre-fill study type
      if (studyOrderId) {
        setLoading(true);
        getStudyOrderInfo(studyOrderId)
          .then((info) => {
            if (info.studyTypeId != null) setStudyTypeId(info.studyTypeId);
            if (info.presumptiveDiagnostic) setPresumptiveDiagnostic(info.presumptiveDiagnostic);
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
      return;
    }

    let active = true;
    setLoading(true);

    getStudyDetail(healthPatientServiceId)
      .then((detail) => {
        if (!active) return;
        setStudyDetail(detail);

        // Pre-fill fields from existing data
        setStudyTypeId(detail.studyTypeId);
        setServiceDate(parseISODate(detail.serviceDate) ?? new Date());
        setOrderedByOption(
          detail.orderedById != null
            ? { id: detail.orderedById, name: detail.orderedByName ?? '' }
            : null,
        );
        setExternalId(detail.externalId ?? '');
        setPresumptiveDiagnostic(detail.presumptiveDiagnostic ?? '');
        setStateTypeId(detail.stateTypeId ?? null);
        setResultDate(parseISODate(detail.resultDate) ?? new Date());
        setResultDescription(detail.resultDescription ?? '');
        setResultText(detail.resultText ?? '');
      })
      .catch((err) => {
        if (!active) return;
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(err, 'No se pudo cargar el detalle del estudio'),
          life: 5000,
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [visible, healthPatientServiceId, isNewMode]);

  // ── Validation ──
  const validateCurrentStep = useCallback((): boolean => {
    if (currentStepKey === 'study') {
      if (!studyTypeId) {
        toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Seleccione un tipo de estudio', life: 3000 });
        return false;
      }
      if (!serviceDate) {
        toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Ingrese la fecha del estudio', life: 3000 });
        return false;
      }
      return true;
    }
    if (currentStepKey === 'result') {
      if (!stateTypeId) {
        toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Seleccione el estado del estudio', life: 3000 });
        return false;
      }
      if (!resultDate) {
        toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Ingrese la fecha del resultado', life: 3000 });
        return false;
      }
      if (!resultDescription.trim()) {
        toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Ingrese una descripción del resultado', life: 3000 });
        return false;
      }
      return true;
    }
    // Determinations step has no required validation
    return true;
  }, [currentStepKey, studyTypeId, serviceDate, stateTypeId, resultDate, resultDescription]);

  // ── Navigation ──
  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;

    const nextIdx = currentStep + 1;
    if (nextIdx >= steps.length) return;

    // When transitioning from determinations to result, pre-fill result text
    if (steps[nextIdx]?.key === 'result' && isLaboratory && determinations.length > 0) {
      if (!resultDescription) {
        setResultDescription(determinations.map((d) => d.description).join(', '));
      }
      if (!resultText) {
        setResultText(
          determinations
            .map((d) => `${d.description}: ${d.value}${d.unit ? ' ' + d.unit : ''}`)
            .join('\n'),
        );
      }
    }

    // When skipping determinations (non-lab), pre-fill from order items
    if (currentStepKey === 'study' && !isLaboratory && !resultDescription && studyDetail?.studyOrderItems) {
      setResultDescription(studyDetail.studyOrderItems);
    }

    setCurrentStep(nextIdx);
  }, [currentStep, steps, validateCurrentStep, isLaboratory, determinations, resultDescription, resultText, currentStepKey, studyDetail]);

  const handlePrev = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // ── Save ──
  const handleConfirm = useCallback(async () => {
    if (!validateCurrentStep()) return;
    if (!studyTypeId || !stateTypeId) return;

    setSaving(true);
    try {
      const request: SaveStudyResultRequest = {
        healthPatientServiceId,
        patientId,
        studyTypeId,
        studyOrderId: studyOrderId ?? null,
        serviceDate: formatDateISO(serviceDate),
        orderedById: orderedByOption?.id ?? null,
        externalId: externalId || null,
        presumptiveDiagnostic: presumptiveDiagnostic || null,
        stateTypeId,
        resultDate: formatDateISO(resultDate),
        resultDescription: resultDescription.trim(),
        resultText: resultText || null,
        tempFileIds: uploadedFiles.map((f) => f.tempFileId),
        determinations: isLaboratory
          ? determinations.map((d) => ({ determinationId: d.determinationId, value: d.value }))
          : [],
      };

      const response = await saveStudyResult(request);

      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Guardado',
          detail: 'El resultado del estudio fue guardado correctamente',
          life: 3000,
        });
        // Delay slightly so the user sees the success message
        setTimeout(() => {
          onSaved();
          onHide();
        }, 500);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.message || 'No se pudo guardar el resultado',
          life: 5000,
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al guardar',
        detail: getErrorMessage(err, 'No se pudo guardar el resultado del estudio'),
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  }, [
    validateCurrentStep,
    healthPatientServiceId,
    patientId,
    studyTypeId,
    serviceDate,
    orderedByOption,
    externalId,
    presumptiveDiagnostic,
    stateTypeId,
    resultDate,
    resultDescription,
    resultText,
    uploadedFiles,
    determinations,
    isLaboratory,
    onSaved,
    onHide,
  ]);

  // ── Stepper renderer ──
  const renderStepper = () => (
    <div className="study-wizard__stepper">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = idx < currentStep;
        const stepClass = [
          'study-wizard__step',
          isActive ? 'study-wizard__step--active' : '',
          isCompleted ? 'study-wizard__step--completed' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={step.key} className={stepClass}>
            <div className="study-wizard__step-bubble">
              <div className="study-wizard__step-number">
                {isCompleted ? (
                  <i className="pi pi-check" style={{ fontSize: '0.75rem' }} />
                ) : (
                  idx + 1
                )}
              </div>
              <span className="study-wizard__step-label">{step.label}</span>
            </div>
            {idx < steps.length - 1 ? (
              <div
                className={`study-wizard__step-line${isCompleted ? ' study-wizard__step-line--completed' : ''}`}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );

  // ── Render step body ──
  const renderBody = () => {
    if (loading) {
      return (
        <div className="study-wizard__loading">
          <ProgressSpinner style={{ width: '40px', height: '40px' }} />
          <span>Cargando estudio...</span>
        </div>
      );
    }

    switch (currentStepKey) {
      case 'study':
        return (
          <StudyDataStep
            patientId={patientId}
            studyDetail={studyDetail}
            studyTypeId={studyTypeId}
            onStudyTypeIdChange={setStudyTypeId}
            serviceDate={serviceDate}
            onServiceDateChange={setServiceDate}
            orderedById={orderedByOption?.id ?? null}
            orderedByOption={orderedByOption}
            onOrderedByChange={setOrderedByOption}
            externalId={externalId}
            onExternalIdChange={setExternalId}
            presumptiveDiagnostic={presumptiveDiagnostic}
            onPresumptiveDiagnosticChange={setPresumptiveDiagnostic}
          />
        );
      case 'determinations':
        return (
          <DeterminationsStep
            determinations={determinations}
            onChange={setDeterminations}
          />
        );
      case 'result':
        return (
          <ResultStep
            stateTypeId={stateTypeId}
            onStateTypeIdChange={setStateTypeId}
            resultDate={resultDate}
            onResultDateChange={setResultDate}
            resultDescription={resultDescription}
            onResultDescriptionChange={setResultDescription}
            resultText={resultText}
            onResultTextChange={setResultText}
            uploadedFiles={uploadedFiles}
            onUploadedFilesChange={setUploadedFiles}
            existingFiles={existingFiles}
            determinations={determinations}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={onHide}
        header={isNewMode ? 'Nuevo resultado de estudio' : 'Cargar resultado de estudio'}
        modal
        className="study-wizard-dialog"
        closable={!saving}
        draggable={false}
        footer={null}
        blockScroll
      >
        <div className="study-wizard">
          {!loading ? renderStepper() : null}

          <div className="study-wizard__body">{renderBody()}</div>

          {!loading ? (
            <div className="study-wizard__footer">
              <div className="study-wizard__footer-left">
                {currentStep > 0 ? (
                  <Button
                    label="Anterior"
                    icon="pi pi-chevron-left"
                    className="p-button-outlined p-button-secondary study-wizard__btn-prev"
                    onClick={handlePrev}
                    disabled={saving}
                  />
                ) : null}
              </div>
              <div className="study-wizard__footer-right">
                {isLastStep ? (
                  <Button
                    label="Confirmar"
                    icon="pi pi-check"
                    className="study-wizard__btn-confirm"
                    onClick={handleConfirm}
                    loading={saving}
                    disabled={!studyTypeId || !serviceDate}
                  />
                ) : (
                  <Button
                    label="Siguiente"
                    icon="pi pi-chevron-right"
                    iconPos="right"
                    className="study-wizard__btn-next"
                    onClick={handleNext}
                    disabled={saving}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>
    </>
  );
}
